import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const { t } = useTranslation();
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState | null)?.from ?? '/admin';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({ email, password });
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="max-w-md mx-auto px-6 py-20">
      <div className="text-center mb-10">
        <span className="editorial-number text-4xl block">N°00</span>
        <div className="editorial-label mt-2">— Atelier · Privé</div>
      </div>
      <h1 className="font-display text-5xl italic text-center mb-12">{t('login.title')}</h1>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div>
          <label htmlFor="email" className="editorial-label block mb-2">
            {t('login.email')}
          </label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-0 py-3 bg-transparent border-b border-linen focus:border-brass outline-none font-display text-xl transition-colors"
          />
        </div>

        <div>
          <label htmlFor="password" className="editorial-label block mb-2">
            {t('login.password')}
          </label>
          <input
            id="password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-0 py-3 bg-transparent border-b border-linen focus:border-brass outline-none font-display text-xl transition-colors"
          />
        </div>

        {error && <div className="font-display italic text-base text-red-800">{error}</div>}

        <button
          type="submit"
          disabled={submitting}
          className="group w-full inline-flex items-center justify-between bg-ink text-ivory px-8 py-4 hover:bg-brass-dark transition-colors disabled:opacity-50"
        >
          <span className="editorial-label text-ivory">
            {submitting ? t('login.submitting') : t('login.submit')}
          </span>
          <span className="font-display text-xl transition-transform group-hover:translate-x-1">→</span>
        </button>
      </form>

      {import.meta.env.VITE_USE_MOCKS === 'true' && (
        <div className="mt-10 pt-6 border-t border-linen">
          <div className="editorial-label text-brass mb-2">— {t('login.mockHint.title')}</div>
          <div className="font-display italic text-base text-ink-soft">
            admin@handcrafted.local · admin123
          </div>
        </div>
      )}
    </section>
  );
}
